import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Edit3, X, ShieldAlert, Mail, ShieldCheck, CheckCircle2, Clock, AlertCircle } from 'lucide-react';
import type { User } from '../../../../shared/types/index.js';

interface EditUserModalProps {
  editingUser: User | null;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  isPending: boolean;
  formName: string;
  setFormName: (val: string) => void;
  formEmail: string;
  formRole: 'penulis' | 'penerbit' | 'organisasi' | 'admin';
  setFormRole: (val: 'penulis' | 'penerbit' | 'organisasi' | 'admin') => void;
  formOrg: string;
  setFormOrg: (val: string) => void;
  formPubCode: string;
  setFormPubCode: (val: string) => void;
  formVerificationStatus?: 'verified' | 'rejected' | 'pending' | 'unverified';
  setFormVerificationStatus?: (val: 'verified' | 'rejected' | 'pending' | 'unverified') => void;
  formVerificationNotes?: string;
  setFormVerificationNotes?: (val: string) => void;
  formError: string | null;
}

export const EditUserModal: React.FC<EditUserModalProps> = ({
  editingUser,
  onClose,
  onSubmit,
  isPending,
  formName,
  setFormName,
  formEmail,
  formRole,
  setFormRole,
  formOrg,
  setFormOrg,
  formPubCode,
  setFormPubCode,
  formVerificationStatus = 'unverified',
  setFormVerificationStatus,
  formVerificationNotes = '',
  setFormVerificationNotes,
  formError,
}) => {
  if (typeof document === 'undefined') return null;

  return createPortal(
    <AnimatePresence>
      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            className="bg-white rounded-3xl max-w-lg w-full border border-slate-200 shadow-2xl overflow-y-auto max-h-[calc(100vh-2rem)] relative"
          >
            <div className="p-6 border-b border-slate-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-indigo-600" />
                <h3 className="font-bold text-slate-950 text-sm">Sunting Akses & Info User</h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Tutup modal edit user"
                className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={onSubmit} className="p-6 space-y-4">
              {formError && (
                <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-150 text-rose-700 text-xs flex items-start gap-2">
                  <ShieldAlert className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{formError}</span>
                </div>
              )}

              <div className="p-3.5 bg-slate-50 rounded-2xl space-y-1 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Email Akun (Tetap)</p>
                <p className="text-xs text-slate-800 font-mono flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-slate-400" />
                  <span>{formEmail}</span>
                </p>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Nama Pengguna
                </label>
                <input
                  type="text"
                  required
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Nama lengkap..."
                  className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs focus:outline-hidden focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition bg-white"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                  Ubah Peran / Role Otoritas
                </label>
                <select
                  value={formRole}
                  onChange={(e) => {
                    const selected = e.target.value as 'penulis' | 'penerbit' | 'organisasi' | 'admin';
                    setFormRole(selected);
                  }}
                  className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs focus:outline-hidden focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition bg-white"
                >
                  <option value="penulis">Penulis (Self-Publisher)</option>
                  <option value="penerbit">Penerbit (Publisher Code Resmi)</option>
                  <option value="organisasi">Organisasi / Komunitas (Literasi)</option>
                  <option value="admin">Platform Administrator</option>
                </select>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                    {formRole === 'organisasi'
                      ? 'Nama Organisasi / Komunitas'
                      : formRole === 'penerbit'
                      ? 'Nama Lembaga Penerbit Resmi'
                      : 'Nama Organisasi / Afiliasi'}
                  </label>
                  {formRole === 'penulis' && (
                    <span className="text-[10px] text-slate-400 font-medium bg-slate-100 px-2 py-0.5 rounded">
                      Dinonaktifkan
                    </span>
                  )}
                </div>
                <input
                  type="text"
                  disabled={formRole === 'penulis'}
                  value={formRole === 'penulis' ? '' : formOrg}
                  onChange={(e) => setFormOrg(e.target.value)}
                  placeholder={
                    formRole === 'penulis'
                      ? 'Dinonaktifkan untuk Penulis Mandiri'
                      : 'Nama organisasi atau penerbit...'
                  }
                  className={`w-full px-3.5 py-2 rounded-xl border text-xs transition ${
                    formRole === 'penulis'
                      ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed select-none'
                      : 'border-slate-200 focus:outline-hidden focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 bg-white'
                  }`}
                />
              </div>

              {(formRole === 'penerbit' || formRole === 'organisasi') && (
                <>
                  <div>
                    <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                      Publisher / Org Code (4-Digit Angka)
                    </label>
                    <input
                      type="text"
                      maxLength={4}
                      value={formPubCode}
                      onChange={(e) => setFormPubCode(e.target.value.replace(/[^0-9]/g, ''))}
                      placeholder="Kosongkan untuk otomatis"
                      className="w-full px-3.5 py-2 rounded-xl border border-slate-200 text-xs font-mono focus:outline-hidden focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-600 transition bg-white"
                    />
                  </div>

                  {/* Status Verifikasi Penerbit / Organisasi */}
                  {setFormVerificationStatus && (
                    <div className="p-4 rounded-2xl bg-purple-50/50 border border-purple-200/80 space-y-3">
                      <div className="flex items-center gap-1.5 text-xs font-bold text-purple-950">
                        <ShieldCheck className="w-4 h-4 text-purple-700" />
                        <span>Status Verifikasi Legalitas {formRole === 'organisasi' ? 'Organisasi' : 'Penerbit'}:</span>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                        {[
                          { val: 'verified', label: 'Terverifikasi (Aktif)', icon: CheckCircle2, color: 'text-emerald-700 bg-emerald-50 border-emerald-300' },
                          { val: 'pending', label: 'Menunggu Review', icon: Clock, color: 'text-amber-700 bg-amber-50 border-amber-300' },
                          { val: 'rejected', label: 'Ditolak / Revisi', icon: ShieldAlert, color: 'text-rose-700 bg-rose-50 border-rose-300' },
                          { val: 'unverified', label: 'Belum Terverifikasi', icon: AlertCircle, color: 'text-slate-700 bg-slate-50 border-slate-300' },
                        ].map((opt) => {
                          const Icon = opt.icon;
                          const isSel = formVerificationStatus === opt.val;
                          return (
                            <button
                              key={opt.val}
                              type="button"
                              onClick={() => setFormVerificationStatus(opt.val as any)}
                              className={`p-2 rounded-xl border text-xs font-semibold flex items-center gap-1.5 transition cursor-pointer text-left ${
                                isSel ? `${opt.color} ring-2 ring-purple-400/30` : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                              }`}
                            >
                              <Icon className="w-3.5 h-3.5 shrink-0" />
                              <span className="truncate">{opt.label}</span>
                            </button>
                          );
                        })}
                      </div>

                      {setFormVerificationNotes && (
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1">
                            Catatan Verifikasi Admin
                          </label>
                          <input
                            type="text"
                            value={formVerificationNotes}
                            onChange={(e) => setFormVerificationNotes(e.target.value)}
                            placeholder="Catatan status verifikasi..."
                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 text-xs focus:outline-hidden focus:ring-1 focus:ring-purple-500 bg-white"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 rounded-xl text-xs font-medium text-slate-600 hover:bg-slate-100 transition cursor-pointer"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={isPending}
                  className="px-5 py-2.5 rounded-xl text-xs font-bold bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white shadow-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {isPending ? 'Menyimpan...' : 'Simpan Perubahan'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
};
